import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import DisplayText from '../text/DisplayText';
import EditableName from '../EditableName';
import EditableCurrency from '../EditableCurrency';
import EditableOwner from '../EditableOwner';
import Item from '../Item';
import ItemList from '../ItemList';
import SelectLabel from '../SelectLabel';
import Select from '../Select';
import SelectMany from '../SelectMany';
import Boolean from '../Boolean';
import Switch from '../Switch';
import Text from '../Text';
import Number from '../Number';
import Currency from '../Currency';
import Date from '../Date';
// import PipelineSummary from '../../../deal/PipelineSummary';

// ------- Import dummy data / help methods

import { createGetSuggestionsFromApiUrl } from '../HelpMethods';
import { testItemAxel, cities, competences, labels, lostReasons, roles } from './MockData';
import { buildHeader, cardStyle } from './HelpMethods';

// ------- Methods

const getConsultantSuggestions = createGetSuggestionsFromApiUrl('api/employee/getConsultantSuggestions');

/**
 * Servers the purpose of demonstrating all crm field inputs.
 */
const CrmInputDemo = () => {
    const [shouldReturnError, setShouldReturnError] = useState(false);

    const handleSaveGiveErrorMessage = async (value) => {
        await new Promise((r) => setTimeout(r, 500));
        setShouldReturnError(value);
        return { success: true };
    };

    const handleSave = async (value) => {
        if (shouldReturnError) {
            await new Promise((r) => setTimeout(r, 500));
            return { success: false };
        }
        await new Promise((r) => setTimeout(r, 1500));
        console.info(`saving [${typeof value}]`, value);
        return { success: true };
    };

    const handleSaveItem = async (item) => {
        if (shouldReturnError) {
            await new Promise((r) => setTimeout(r, 500));
            return { success: false };
        }
        const newItem = JSON.parse(JSON.stringify(item));
        if (item && item.id === null) {
            newItem.id = uuidv4();
            newItem.link = `/person/${newItem.id}`;
        }
        await new Promise((r) => setTimeout(r, 1500));
        console.info(`saving [${typeof item}]`, item);
        return { success: true, value: newItem };
    };

    const handleSaveItems = async (items) => {
        if (shouldReturnError) {
            await new Promise((r) => setTimeout(r, 500));
            return { success: false };
        }
        const newItems = JSON.parse(JSON.stringify(items));
        newItems.forEach((item) => {
            if (item.id === null) {
                item.id = uuidv4();
                item.link = `/person/${item.id}`;
            }
        });
        await new Promise((r) => setTimeout(r, 1500));
        console.info(`saving [${typeof items}]`, items);
        return { success: true, value: newItems };
    };

    return (
        <>
            <div className='card' style={cardStyle}>
                {buildHeader(
                    'Om sidan',
                    null,
                    'Visar de field-input componenter som finns i crm:et. <br/>Komponenterna är generaliserade, exempelvis används samma komponent för att välja miljö/ort/roll. <br/>Enbart mock-data används i frontenden - inget sparas till db.<br/>Felmeddelanden ges om användaren fyller i felaktig data eller något gick fel på serversidan. Toggla nedan för att testa felmeddelande.'
                )}
                <Boolean label='Ge felmeddelande' initialValue={shouldReturnError} handleSave={handleSaveGiveErrorMessage} isSwitch />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Text', 'DisplayText', 'Används för att visa diverse texter och paragrafer')}
                <div className='m-3'>
                    <DisplayText value={'peter.aslund@consid.se'} />
                </div>
                <div className='m-3'>
                    <i>Som innehåller email</i>
                    <DisplayText value={'Min text som innehåller en email: peter.aslund@consid.se'} />
                </div>
                <div className='m-3'>
                    <i>Med fast länk</i>
                    <DisplayText value={'Min text som innehåller en email: peter.aslund@consid.se'} urlLink='https://alltid.consid.net/' />
                </div>
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Redigera namn', 'EditableName', 'Används för att redigera namnet på kort/person/företag.')}
                <div className='m-3'>
                    <EditableName
                        description={'Döp om person:'}
                        customDisplayWrapper={(v) => <h1 style={{ fontWeight: 'normal', margin: 0 }}>{v}</h1>}
                        initialValue={'Förnamn Efternamn'}
                        handleSave={handleSave}
                    />
                </div>
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Redigera belopp', 'EditableCurrency', 'Används för att redigera beloppet för en deal.')}
                <div className='m-3'>
                    <EditableCurrency
                        description={'Ändra belopp:'}
                        customDisplayWrapper={(v) => <h2 style={{ fontWeight: 'normal', margin: 0 }}>{v}</h2>}
                        initialValue={200000}
                        handleSave={handleSave}
                    />
                </div>
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Redigera ägare', 'EditableOwner', 'Används för att redigera ägare för kort/person/företag.')}
                <div className='m-3'>
                    <EditableOwner initialValue={testItemAxel} handleSave={handleSave} />
                </div>
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader(
                    'Välj ett företag/person',
                    'Item',
                    'Används för att välja konsult/person/företag. Suggestions hämtas från backend eftersom man skriver. Företag och person kommer gå att skapa, inte konsulter.'
                )}
                <Item
                    label='Konsult'
                    icon='person'
                    initialValue={null}
                    getSuggestions={getConsultantSuggestions}
                    handleSave={handleSaveItem}
                />
                <Item
                    label={
                        <>
                            Person <br /> (kan skapa)
                        </>
                    }
                    icon='person'
                    initialValue={null}
                    getSuggestions={getConsultantSuggestions}
                    handleSave={handleSaveItem}
                    isCreationPermitted={true}
                />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader(
                    'Välj flera företag/personer',
                    'ItemList',
                    'Används för att välja <b>flera</b> konsult/person/företag. Suggestions hämtas från backend eftersom man skriver. Företag och person kan skapas, inte konsulter.'
                )}
                <ItemList
                    label={'Consid-konsulter'}
                    icon='person'
                    initialValue={[testItemAxel]}
                    getSuggestions={getConsultantSuggestions}
                    handleSave={handleSaveItems}
                />
                <ItemList
                    label={
                        <>
                            Personer <br /> (kan skapa)
                        </>
                    }
                    icon='person'
                    initialValue={[testItemAxel]}
                    getSuggestions={getConsultantSuggestions}
                    handleSave={handleSaveItems}
                    isCreationPermitted
                />
                <Text label='Ny konsult (fritext)' initialValue={null} handleSave={handleSave} />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Välj företags-label', 'SelectLabel')}
                <SelectLabel initialValue={null} options={labels} handleSave={handleSave} />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Välj', 'Select')}
                <Select icon='lost-reason' label='Förlustorsak' initialValue={null} options={lostReasons} handleSave={handleSave} />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Välj flera', 'SelectMany')}
                <SelectMany
                    label={'Teknisk Miljö'}
                    icon='technical-environment'
                    initialValue={[]}
                    options={competences}
                    handleSave={handleSave}
                />
                <SelectMany label={'Ort'} icon='place' initialValue={[]} options={cities} handleSave={handleSave} />
                <SelectMany label={'Roll'} icon='role' initialValue={[]} options={roles} handleSave={handleSave} />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Boolean / Switch', 'Boolean/Switch')}
                <Boolean label='Boolean' initialValue={null} handleSave={handleSave} />
                <Switch label='Switch' initialValue={null} handleSave={handleSave} isSwitch />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Fritext', 'Text')}
                <Text label='Notat' initialValue={null} handleSave={handleSave} />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Validering / Serverfel', 'Text', 'Om något går fel på serversidan eller användaren angivet ogiltig input.')}
                <Text
                    label='Notat'
                    initialValue={''}
                    handleSave={async () => {
                        await new Promise((r) => setTimeout(r, 500));
                        return { success: false };
                    }}
                />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Tal / Valutor', 'Number/Currency')}
                <Number label='Tal' initialValue={10} handleSave={handleSave} />
                <Number label='Procent % (validering)' initialValue={null} handleSave={handleSave} isInt isPercentage />
                <Currency label='Timpris' initialValue={null} handleSave={handleSave} />
            </div>
            <div className='card' style={cardStyle}>
                {buildHeader('Datum', 'Date')}
                <Date label='Lost date' initialValue={null} handleSave={handleSave} />
            </div>
            {/* <div className='card' style={headerCardStyle}>
                {buildHeader('Pipeline', 'PipelineSummary')}
                <Boolean
                    label='Deal-status (won/lost)'
                    initialValue={isWon}
                    handleSave={async (v) => {
                        await new Promise((r) => setTimeout(r, 500));
                        setIsWon(v);
                        return { success: true };
                    }}
                />
                <div style={{ margin: '10px 20px' }}>
                    <PipelineSummary
                        pipeline={pipelines[0]}
                        pipelineStage={pipelines[0].pipelineStages[2]}
                        pipelines={pipelines}
                        handleSave={handleSave}
                        dealStatus={isWon}
                    />
                </div>
            </div> */}
            <div className='card' style={{ width: '400px', height: '300px', margin: '20px' }}>
                {buildHeader('...')}
            </div>
        </>
    );
};

export default CrmInputDemo;
